import { createContext, useContext, useState, ReactNode, useRef, useEffect, useCallback, useMemo } from 'react';
import LoadingModal from '../components/LoadingModal';
import LoginModal from '../components/LoginModal';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { SESSION_EXPIRED_EVENT, isFreshSessionToken } from '../util/sessionExpiry';

const LOADING_FAILSAFE_MS = 45_000;

interface UIContextType {
    loadingMessage: string | null;
    setLoadingMessage: (message: string | null) => void;
    loginModalOpen: boolean;
    setLoginModalOpen: (open: boolean) => void;
    statusMessage: ReactNode | null;
    setStatusMessage: (message: ReactNode | null) => void;
    queueReRun: <T extends any[]>(fn: (...args: T) => void, ...args: T) => void;
    activeDepot: number;
    setActiveDepot: (depot: number) => void;
    t: (key: string, options?: Record<string, unknown>) => string;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider = ({ children }: { children: ReactNode }) => {
    const [loadingMessage, setLoadingMessageState] = useState<string | null>(null);
    const [loginModalOpen, setLoginModalOpenState] = useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<ReactNode | null>(null);
    const [activeDepot, setActiveDepot] = useState<number>(-1);

    const { t } = useTranslation();
    const { session } = useAuth();
    const location = useLocation();
    const isLoginPage = location.pathname === '/login';

    type QueuedFn<T extends any[] = any[]> = {
        fn: (...args: T) => void;
        args: T;
    };

    const reRunFunctionsRef = useRef<QueuedFn[]>([]);
    const rejectedTokenRef = useRef<string | null>(null);
    const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loginOpenRef = useRef(false);

    const clearLoadingTimer = () => {
        if (loadingTimerRef.current) {
            clearTimeout(loadingTimerRef.current);
            loadingTimerRef.current = null;
        }
    };

    const setLoadingMessage = useCallback((message: string | null) => {
        // Re-auth owns the screen; never stack a spinner on the login form.
        if (message && loginOpenRef.current) return;
        setLoadingMessageState(message);
        clearLoadingTimer();
        if (message) {
            loadingTimerRef.current = setTimeout(() => {
                setLoadingMessageState(null);
                loadingTimerRef.current = null;
            }, LOADING_FAILSAFE_MS);
        }
    }, []);

    // Stable identity: consumers list this in useCallback / useEffect deps, and a
    // new function each render re-fires their fetch effects in a loop.
    const queueReRun = useCallback(<T extends any[]>(fn: (...args: T) => void, ...args: T) => {
        reRunFunctionsRef.current.push({ fn, args });
    }, []);

    const drainReRunQueue = () => {
        if (reRunFunctionsRef.current.length === 0) return;
        const queued = reRunFunctionsRef.current;
        reRunFunctionsRef.current = [];
        queued.forEach(({ fn, args }) => {
            fn(...args);
        });
    };

    // Never open the re-auth modal on the full-page login route (form is already there).
    const setLoginModalOpen = useCallback((open: boolean) => {
        if (open && (window.location.pathname === '/login')) {
            return;
        }
        loginOpenRef.current = open;
        if (open) {
            rejectedTokenRef.current = session?.access_token || null;
            setLoadingMessage(null);
        }
        setLoginModalOpenState(open);
    }, [session?.access_token, setLoadingMessage]);

    // Close the re-auth modal only after a *new* usable token, then replay
    // the requests that 401'd. Closing because the old JWT still decodes is
    // what flickered the POS: modal open → auto-close → queued retry → 401.
    useEffect(() => {
        if (!loginModalOpen) return;
        if (!isFreshSessionToken(session?.access_token, rejectedTokenRef.current)) return;
        loginOpenRef.current = false;
        setLoginModalOpenState(false);
        rejectedTokenRef.current = null;
        drainReRunQueue();
    }, [session?.access_token, session?._syncAt, loginModalOpen]);

    useEffect(() => {
        const onExpired = () => setLoginModalOpen(true);
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, [setLoginModalOpen]);

    useEffect(() => () => clearLoadingTimer(), []);

    // If we land on /login with the re-auth modal still open, drop it.
    useEffect(() => {
        if (isLoginPage && loginModalOpen) {
            loginOpenRef.current = false;
            setLoginModalOpenState(false);
        }
    }, [isLoginPage, loginModalOpen]);

    // Memoised so a spinner or status-message update does not hand every consumer
    // a fresh object (and fresh callbacks) to depend on.
    const contextValue = useMemo(
        () => ({
            loadingMessage,
            setLoadingMessage,
            loginModalOpen,
            setLoginModalOpen,
            statusMessage,
            setStatusMessage,
            queueReRun,
            activeDepot,
            setActiveDepot,
            t,
        }),
        [
            loadingMessage,
            setLoadingMessage,
            loginModalOpen,
            setLoginModalOpen,
            statusMessage,
            queueReRun,
            activeDepot,
            t,
        ],
    );

    return (
        <UIContext.Provider value={contextValue}>
            {children}
            {/* Loading Modal */
                loadingMessage && (
                    <LoadingModal
                        message={loadingMessage} />
                )}


            {/* Login Modal — never stack on the full-page /login route */
                loginModalOpen && !isLoginPage && (
                    <LoginModal
                        open={loginModalOpen}
                        setOpen={setLoginModalOpen}
                    />
                )}
        </UIContext.Provider>
    );
};

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
};
